import React, { useEffect, useRef, useState, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../layouts";
import { useIndividualStore } from "../../store";
import { validateIndividualRenewForm } from "../../services/validators";
import axios from "axios";
import { apiBaseUrl, grecaptchaSiteKey } from "../../services/http";
import Breadcrumb from "../../components/Breadcrumb";
import { Dialog, Transition } from "@headlessui/react";

export default function RenewFormConfirmation() {
  const formRef = useRef();
  const [isOpen, setOpen] = useState(false);
  const navigate = useNavigate();
  const store = useIndividualStore();
  const [estFee, setEstFee] = useState(null);
  const [submission, setSubmission] = useState({
    status: "idle",
    data: null,
    error: null,
  });

  useEffect(() => {
    const validated = validateIndividualRenewForm(store.form);
    if (validated.length > 0) {
      navigate("/individual");
      return;
    }

    const form = { mType: "individual", appType: "renew", ...store.form };

    const fetchEstFee = async () => {
      const response = await axios.post(`${apiBaseUrl}/est-fee`, form, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      setEstFee(response.data);
    };

    fetchEstFee();
  }, [navigate, store.form]);

  useEffect(() => {
    if (!document.querySelector("script#grecaptcha-script")) {
      const script = document.createElement("script");
      script.id = "grecaptcha-script";
      script.src = `https://www.google.com/recaptcha/api.js?render=${grecaptchaSiteKey}`;
      document.body.appendChild(script);
    }

    window.scrollTo(0, 0);
    window.addEventListener("beforeunload", alertBeforeLeaving);
    return () => {
      window.removeEventListener("beforeunload", alertBeforeLeaving);
    };
  }, []);

  useEffect(() => {
    if (submission.status === "succeeded") {
      navigate(`/individual/app/renew/completion`);
    }
  }, [navigate, submission]);

  useEffect(() => {
    if (submission.status === "failed") {
      setOpen(true);
    }
  }, [submission]);

  const alertBeforeLeaving = (e) => {
    e.preventDefault();
    e.returnValue = "";
  };

  const onSubmit = () => {
    setSubmission((state) => ({
      status: "pending",
      data: null,
      error: null,
    }));

    const submitForm = async (form) => {
      try {
        const response = await axios.post(
          `${apiBaseUrl}/individual/renew`,
          form,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        setSubmission((state) => ({
          status: "succeeded",
          data: response.data,
          error: null,
        }));
        store.updateRef(response.data.ref);
      } catch (err) {
        setSubmission((state) => ({
          status: "failed",
          data: null,
          error: err.response.data,
        }));
      }
    };

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(grecaptchaSiteKey, { action: "submit" })
        .then((token) => {
          const form = { mType: "individual", appType: "renew", ...store.form };
          submitForm({
            ...form,
            estFee: estFee.est_fee,
            grecaptcha: token,
          });
        });
    });
  };

  return (
    <Layout mType="individual">
      <div className="bg-gray-100">
        <div className="container mx-auto">
          <div className="lg:py-5 lg:px-0 px-0 py-4">
            <ul className="form-steps">
              <li className="active">Declaration</li>
              <li className="active">Form</li>
              <li className="active">Confirmation</li>
              <li>Completion</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container mx-auto" ref={formRef}>
        <div className="lg:py-8 lg:px-0 px-4 py-4">
          <Breadcrumb mType="individual" appType="renew" />

          <div>
            <div className="border large-shadow mb-8">
              <div className="px-8 py-8">
                <div className="mb-8">
                  <h3 className="font-bold mb-4">
                    Membership, Application and Card Type:
                  </h3>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Membership Type:</label>
                    </div>
                    <div className="flex-1 ml-4 text-blue-700">Individual</div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Application:</label>
                    </div>
                    <div className="flex-1 ml-4 text-blue-700">
                      Card Renewal
                    </div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Card Type:</label>
                    </div>
                    <div className="flex-1 ml-4 text-blue-700">
                      {store.form.cardType === "reader" && "Reader Card"}
                      {store.form.cardType === "borrower" && "Borrower Card"}
                    </div>
                  </div>
                </div>
                {store.form.cardType === "borrower" && (
                  <div className="mb-8">
                    <h3 className="font-bold mb-4">Card Pickup Location:</h3>
                    <div className="flex items-center mb-4 border-b pb-4">
                      <div className="lg:w-56 w-1/2">
                        <label>Pickup Location:</label>
                      </div>
                      <div className="flex-1 ml-4">
                        {store.form.pickupLocation === "mmw" && "MMW Library"}
                        {store.form.pickupLocation === "tkosc" &&
                          "TKOSC Library"}
                      </div>
                    </div>
                  </div>
                )}
                <div className="mb-8">
                  <h3 className="font-bold mb-4">Personal Information:</h3>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Surname:</label>
                    </div>
                    <div className="flex-1 ml-4">{store.form.surname}</div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Other Names:</label>
                    </div>
                    <div className="flex-1 ml-4">{store.form.otherNames}</div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Chinese Name:</label>
                    </div>
                    <div className="flex-1 ml-4">
                      {store.form.chineseName || <i>N/A</i>}
                    </div>
                  </div>
                  {store.form.cardType === "borrower" && (
                    <div className="lg:flex mb-4 border-b pb-4">
                      <div className="lg:w-56">
                        <label>Passport Photo:</label>
                      </div>
                      <div className="lg:flex-1 lg:ml-4">
                        <div className="w-40 h-48">
                          <img
                            src={store.form.photo}
                            alt=""
                            className="h-full w-auto object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Identity Document:</label>
                    </div>
                    <div className="flex-1 ml-4">
                      <span className="bg-blue-100 rounded px-2 py-1">
                        {store.form.idType === "passport" ? "Passport" : "HKID"}
                      </span>{" "}
                      {store.form.idNumber}
                    </div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Institute:</label>
                    </div>
                    <div className="flex-1 ml-4">{store.form.institute}</div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Position:</label>
                    </div>
                    <div className="flex-1 ml-4">{store.form.position}</div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Home Address:</label>
                    </div>
                    <div className="flex-1 ml-4">{store.form.addrHome}</div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Contact Address:</label>
                    </div>
                    <div className="flex-1 ml-4">
                      {store.form.addrContact || <i>Same as Home Address</i>}
                    </div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Phone Number:</label>
                    </div>
                    <div className="flex-1 ml-4">{store.form.phone}</div>
                  </div>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>E-mail:</label>
                    </div>
                    <div className="flex-1 ml-4">{store.form.email}</div>
                  </div>
                </div>
                <div className="mb-8">
                  <h3 className="font-bold mb-4">Required Document:</h3>
                  <div className="flex items-center mb-4 border-b pb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Recommendation Letter From Your Employer:</label>
                    </div>
                    <div className="flex-1 ml-4">1 file selected</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border large-shadow mb-8">
              <div className="px-8 py-8">
                <div>
                  <h3 className="font-bold mb-4">Total amount payable:</h3>
                  <div className="flex mb-4">
                    <div className="lg:w-56 w-1/2">
                      <label>Total amount payable: ^</label>
                    </div>
                    <div className="flex-1 ml-4">
                      {estFee && (
                        <div>
                          {estFee.est_fee === 0 ? (
                            <p>No payment required</p>
                          ) : (
                            <b>{estFee.est_fee}</b>
                          )}
                          <ul className="list-disc text-sm text-gray-600 mt-1 ml-5">
                            {estFee.items.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="italic text-sm text-gray-500">
                    ^ Subject to confirmation
                  </p>
                </div>
              </div>
            </div>

            {submission.status === "pending" ? (
              <div className="flex items-center justify-center my-8">
                <button
                  className="border rounded-full px-4 py-2 w-48 text-center inline-block text-gray-600 hover:bg-gray-200 transition duration-200 focus:outline-none opacity-50 pointer-events-none"
                  disabled={true}
                >
                  Edit
                </button>
                <button
                  className="border border-orange-600 rounded-full px-4 py-2 w-48 text-center inline-flex items-center justify-center bg-orange-600 text-white ml-8 hover:bg-orange-700 transition duration-200 focus:outline-none opacity-50 pointer-events-none"
                  disabled={true}
                >
                  <svg
                    aria-hidden="true"
                    class="inline-block w-4 h-4 mr-1 text-gray-200 animate-spin fill-orange-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  Submit
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center my-8">
                <button
                  className="border rounded-full px-4 py-2 w-48 text-center inline-block text-gray-600 hover:bg-gray-200 transition duration-200 focus:outline-none"
                  onClick={() => navigate(`/individual/app/renew/form`)}
                >
                  Edit
                </button>
                <button
                  className="border border-orange-600 rounded-full px-4 py-2 w-48 text-center inline-block bg-orange-600 text-white ml-8 hover:bg-orange-700 transition duration-200 focus:outline-none"
                  onClick={onSubmit}
                >
                  Submit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div class="sm:flex sm:items-start mb-4">
                    <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg
                        class="h-6 w-6 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                        />
                      </svg>
                    </div>
                    <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <h3
                        class="text-base font-semibold leading-6 text-gray-900"
                        id="modal-title"
                      >
                        Error
                      </h3>
                      <div class="mt-2">
                        <p class="text-sm text-gray-500">
                          {submission.error?.message}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="sm:flex sm:flex-row-reverse">
                    <button
                      onClick={() => setOpen(false)}
                      type="button"
                      class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
